import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, Subscription, forkJoin, throwError } from 'rxjs';
import { SpinnerService } from './spinner.service';
import { UtilService } from './util.service';
import { catchError, finalize, map } from 'rxjs/operators';
import { LoginModel, SendNotificationModel, AccountPasswordModel, TokenResponseModel, ServicePlus, TYPE, Condition, ApiResponseModel, TwoFactorAuthentication, PlusException, OtpNotificationModel, OtpModel } from '../models';
import { PlusConfig } from '../environments/plus-enviroment';
import { ServiceLocator } from '../ServiceLocator';
import { ClearLoggedUserAction, ClearUserRolesAction, SaveLoggedUserAction, SaveUserOrganizationsAction, SaveUserRolesAction } from '../store/actions';
import { AppState } from '../models/AppStateModel';
import { HttpService } from './http.service';
import { UserOrganizations } from '../models/UserOrganizations';
import { StoreService } from './store.service';

@Injectable()
export class AuthService {

  public spinnerService: SpinnerService;
  public utilService: UtilService;
  public loggedUser: TokenResponseModel;
  public userRoles : string[];
  public userOrganizations : UserOrganizations[];
  public userRoles$ : Subject<string[]> = new Subject<string[]>();

  public userSocket: any;

  constructor(private http: HttpClient, private appStore: Store<AppState>,private httpService: HttpService, private storeService : StoreService) {
    this.spinnerService = ServiceLocator.injector.get(SpinnerService);
    this.utilService = ServiceLocator.injector.get(UtilService);

    this.appStore.select(store => store).subscribe((state: any) => {
      this.loggedUser = state.loggedUser;
      let userRolesString = localStorage.getItem('userRoles');
      let userRolesArray : string[];
      if (userRolesString) {
        userRolesArray = userRolesString.split(',');
      }
      if(!(state.userRoles == null ||state.userRoles == undefined)){
        this.userRoles = state.userRoles;
      }else if(userRolesArray){
        this.userRoles = userRolesArray;  
      }
      let userOrganizationsString = localStorage.getItem('userOrganizations');
      this.userOrganizations = userOrganizationsString ? JSON.parse(userOrganizationsString) : [];
    });
  }


  /******************************* PREVIEW INFO *******************************/
  public getLoginPreviewInfo(userIdentifier: string, source: string = 'LOGIN'): Observable<any> {
    let request: Observable<Object>;

    if (PlusConfig.API.IS_DMZ)
      request = this.buildPreviewInfoDMZRequest(userIdentifier, source);
    else
      throw new Error("Servicio no disponible para ApiCore");

    this.spinnerService.show();
    return request.pipe(
      map(x => new ApiResponseModel(x)),
      catchError((error) => {
      return this.handleResponseError(error)
      }),
      finalize(() => this.spinnerService.hide()));

  }

  public getLoginPreviewInfoAuthSva(userIdentifier: string, source: string = 'LOGIN'): Observable<any> {
    let request: Observable<Object>;

    if (PlusConfig.API.IS_DMZ)
      request = this.buildPreviewInfoAuthSvaDMZRequest(userIdentifier, source);
    else
      throw new Error("Servicio no disponible para ApiCore");

    this.spinnerService.show();
    return request.pipe(
      map(x => new ApiResponseModel(x)),
      catchError((error) => {
      return this.handleResponseError(error)
      }),
      finalize(() => this.spinnerService.hide()));

  }

  private buildPreviewInfoDMZRequest(userIdentifier: string, source: string): Observable<Object> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'x-api-key': PlusConfig.API.APIKEY
    });


    return this.http.get(PlusConfig.API.ENDPOINT + '/api/SigmaApps/CL/Serv/GetCustomerPreviewInfo/' + userIdentifier + '/' + source, { headers: headers });
  }

  private buildPreviewInfoAuthSvaDMZRequest(userIdentifier: string, source: string): Observable<Object> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'x-api-key': PlusConfig.API.APIKEY
    });


    return this.http.get(PlusConfig.API.ENDPOINT + '/api/SigmaApps/CL/Serv/GetCustomerPreviewInfoDetails/' + userIdentifier + '/' + source, { headers: headers });
  }

  /******************************* LOG IN *******************************/
  public logIn(userIdentifier: string, password: string, flushCode: string = null): Observable<Object> {
    if (this.loggedUser)
      this.logOut();

    let request: Observable<Object>;

    if (PlusConfig.API.IS_DMZ)
      request = this.buildLoginDMZRequest(userIdentifier, password, flushCode);
    else
      request = this.buildLoginCoreRequest(userIdentifier, password, flushCode);

    this.spinnerService.show();
    return request.pipe(
      map(x => {
        let trm = new TokenResponseModel(x);
        this.saveToken(trm);
        let userCode = trm.user.user_code; 
        this.requestUserRoles(userCode);     
        this.getUserOrganizations(trm.user.user_id);   
        return trm;
      }),
      catchError((error) => {
        return this.handleResponseError(error)
      }),
      finalize(() => this.spinnerService.hide()));
  }

  public preliminaryLogin(userIdentifier: string, password: string): Observable<string> {
    let request: Observable<Object>;
  
    if (PlusConfig.API.IS_DMZ)
      request = this.buildLoginDMZRequest(userIdentifier, password);
    else
      request = this.buildLoginCoreRequest(userIdentifier, password);
  
    this.spinnerService.show();
    return request.pipe(
      map(x => {
        let trm = new TokenResponseModel(x);
        return trm.access_token;
      }),
      catchError((error) => {
        return this.handleResponseError(error);
      }),
      finalize(() => this.spinnerService.hide()));
  }  

  private buildLoginCoreRequest(userIdentifier: string, password: string, flushCode: string = null): Observable<Object> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    let body = new URLSearchParams();

    if (!flushCode)
      body.set('grant_type', 'password');
    else {
      body.set('grant_type', 'login_flush');
      body.set('flushcode', flushCode);
    }

    body.set('username', userIdentifier);
    body.set('password', password);

    return this.http.post(PlusConfig.API.ENDPOINT + '/Token', body.toString(), { headers: headers });
  }

  private buildLoginDMZRequest(userIdentifier: string, password: string, flushCode: string = null): Observable<Object> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8'
    });
    let loginModel = new LoginModel();
    loginModel.identifier = userIdentifier;
    loginModel.password = password;
    loginModel.flushCode = flushCode;
    return this.http.post(PlusConfig.API.ENDPOINT + '/api/account/Login', loginModel, { headers: headers });
  }

  /******************************* LOG OUT *******************************/
  public logOut(cleanaup: boolean = false): Subscription {
    if (!this.loggedUser)
      throw new Error("No logged user");

    let request: Observable<Object>;

    if (PlusConfig.API.IS_DMZ)
      request = this.buildLogOutDMZRequest();
    else
      request = this.buildLogOutCoreRequest();

    this.spinnerService.show();
    this.appStore.dispatch(new ClearLoggedUserAction());
    this.appStore.dispatch(new ClearUserRolesAction())
    this.storeService.deleteAllStates();

    if (cleanaup) {
      localStorage.clear();
      sessionStorage.clear();
    } else {
      localStorage.removeItem('loggedUser');
      localStorage.removeItem('formsApplet');
      localStorage.removeItem('userRoles');
      localStorage.removeItem('userOrganizations');
      sessionStorage.removeItem('breadCrumbs');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('SessionID');
      sessionStorage.removeItem('UILBehavior');
    }

    return request.pipe(
      catchError((error) => {
        return this.handleResponseError(error)
      }),
      finalize(() => this.spinnerService.hide()))
      .subscribe(x => { });
  }

  private buildLogOutCoreRequest(): Observable<Object> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'Authorization': this.loggedUser.token_type + ' ' + this.loggedUser.access_token
    });

    return this.http.post(PlusConfig.API.ENDPOINT + '/api/Account/Logout', null, { headers: headers });
  }

  private buildLogOutDMZRequest(): Observable<Object> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'Authorization': this.loggedUser.token_type + ' ' + this.loggedUser.access_token
    });

    return this.http.post(PlusConfig.API.ENDPOINT + '/api/Account/Logout', null, { headers: headers });
  }

  /******************************* REFRESH SESSION *******************************/
  public refreshSession(): Subscription {
    if (!this.loggedUser)
      throw new Error("No logged user");

    let request: Observable<Object>;

    if (PlusConfig.API.IS_DMZ)
      request = this.buildRefreshSessionDMZRequest();
    else
      request = this.buildRefreshSessionCoreRequest();

    this.spinnerService.show();
    this.appStore.dispatch(new ClearLoggedUserAction());

    return request.pipe(
      map(x => new TokenResponseModel(x)),
      catchError((error) => {
        return this.handleResponseError(error)
      }),
      finalize(() => this.spinnerService.hide()))
      .subscribe(trm => {
        // console.log(trm);
        this.saveToken(trm);
      });
  }

  private buildRefreshSessionCoreRequest(): Observable<Object> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'grant_type': 'refresh_token',
      'refresh_token': this.loggedUser.refresh_token
    });

    return this.http.post(PlusConfig.API.ENDPOINT + '/Token', null, { headers: headers });
  }

  private buildRefreshSessionDMZRequest(): Observable<Object> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8'
    });
    let payload = new LoginModel();
    payload.flushCode = this.loggedUser.refresh_token

    return this.http.post(PlusConfig.API.ENDPOINT + '/api/Account/RefreshToken', payload, { headers: headers });
  }

  /******************************* SEND RESET PASSWORD CODE *******************************/
  public sendResetCode(userIdentifier: string, verifyInfo: string, targetMedia: TwoFactorAuthentication): Observable<any> {
    let request: Observable<Object>;

    if (PlusConfig.API.IS_DMZ)
      request = this.buildSendResetCodeDMZRequest(userIdentifier, verifyInfo, targetMedia);
    else
      request = this.buildSendResetCodeCoreRequest(userIdentifier, verifyInfo, targetMedia);

    this.spinnerService.show();
    return request.pipe(
      catchError((error) => {
        return this.handleResponseError(error)
      }),
      finalize(() => this.spinnerService.hide())
    );
  }

  private buildSendResetCodeCoreRequest(userIdentifier: string, verifyInfo: string, targetMedia: TwoFactorAuthentication): Observable<Object> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8'
    });
    let payload = {
      'UserCode': userIdentifier,
      'Verify': verifyInfo,
      'MediaTarget': targetMedia
    };

    return this.http.post(PlusConfig.API.ENDPOINT + '/api/PublicAccount/ForgotPassword', payload, { headers: headers });
  }

  private buildSendResetCodeDMZRequest(userIdentifier: string, verifyInfo: string, targetMedia: TwoFactorAuthentication): Observable<Object> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8'
    });
    let resetModel = new SendNotificationModel();
    resetModel.identifier = userIdentifier;
    resetModel.contactVerify = verifyInfo;
    resetModel.mediaTarget = targetMedia;

    return this.http.post(PlusConfig.API.ENDPOINT + '/api/account/SendResetCode', resetModel, { headers: headers });
  }

  /******************************* RESET PASSWORD *******************************/
  public setNewPassword(userIdentifier : string, newPassword : string, confirmPassword : string, authorizationCode : string): Observable<any> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8'
    });

    const payload : any = {
      UserIdentifier : userIdentifier,
      AuthCode : authorizationCode,
      NewPassword : newPassword,
      ConfirmPassword : confirmPassword
    }
    return this.http.post(PlusConfig.API.ENDPOINT + '/api/account/SetNewPassword', payload, { headers: headers });
  }

  public resetPassword(authorizationCode: string, newPassword: string, confirmPassword: string, userType: string = null, isRawToken : boolean): Observable<any> {
    let request: Observable<Object>;

    if (PlusConfig.API.IS_DMZ)
      request = this.buildResetPasswordDMZRequest(newPassword, confirmPassword, authorizationCode, userType, isRawToken);
    else
      request = this.buildResetPasswordCoreRequest(newPassword, confirmPassword, authorizationCode, userType);

    this.spinnerService.show();
    return request.pipe(
      catchError((error) => {
        return this.handleResponseError(error)
      }),
      finalize(() => this.spinnerService.hide())
    );
  }

  private buildResetPasswordCoreRequest(newPassword: string, confirmPassword: string, verificationCode: string, userType: string): Observable<Object> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8'
    });
    let resetModel = new AccountPasswordModel();
    resetModel.newPassword = newPassword;
    resetModel.confirmPassword = confirmPassword;
    resetModel.authorizationCode = verificationCode;
    resetModel.userType = userType;

    return this.http.post(PlusConfig.API.ENDPOINT + '/api/PublicAccount/ChangePassword', resetModel, { headers: headers });
  }

  private buildResetPasswordDMZRequest(newPassword: string, confirmPassword: string, verificationCode: string, userType: string, isRawToken : boolean): Observable<Object> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8'
    });
    let resetModel = new AccountPasswordModel();
    resetModel.newPassword = newPassword;
    resetModel.confirmPassword = confirmPassword;
    resetModel.authorizationCode = verificationCode;
    resetModel.userType = userType;
    resetModel.isRawToken = isRawToken;

    return this.http.post(PlusConfig.API.ENDPOINT + '/api/account/ResetPassword', resetModel, { headers: headers });
  }

  /******************************* OTP *******************************/
  public sendOTP(module: string, operation: string, targetMedia: string): Observable<any> {
    if (!this.loggedUser)
      throw new Error("No logged user");

    let request: Observable<Object>;

    if (PlusConfig.API.IS_DMZ)
      request = this.buildOTPDMZRequest(module, operation, targetMedia);
    else
      request = this.buildOTPCoreRequest(module, operation, targetMedia);

    this.spinnerService.show();
    return request.pipe(
      catchError((error) => {
        return this.handleResponseError(error)
      }),
      finalize(() => this.spinnerService.hide())
    );
  }

  private buildOTPCoreRequest(module: string, operation: string, targetMedia: string): Observable<Object> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'Authorization': this.loggedUser.token_type + ' ' + this.loggedUser.access_token
    });

    let serv = new ServicePlus('SECURITY', 'SER_SEND_OTP_CODE_NOTIF', TYPE.SERVICE);
    serv.Source = 'UIL';
    serv.Conditions = new Condition();
    serv.Conditions.addConditionField("MODULE", module);
    serv.Conditions.addConditionField("OPERATION_NAME", operation);
    serv.Conditions.addConditionField("NOTIF_TYPE", targetMedia);
    serv.Conditions.addConditionField("AUTH_MODE", 'OTP');

    let userIdentifier = this.utilService.getStringAttribute('USER_CODE', this.loggedUser.user.ArrayEntities.List);

    if (!userIdentifier)
      userIdentifier = this.utilService.getStringAttribute('DOC_IDENTIFICACION', this.loggedUser.user.ArrayEntities.List);

    serv.Conditions.addConditionField("IDENTIFIER", userIdentifier);

    return this.http.post(PlusConfig.API.ENDPOINT + '/api/v1/ActionService/ExecuteBService_RetrieveADNList_NoData', serv, { headers: headers });
  }

  private buildOTPDMZRequest(module: string, operation: string, targetMedia: string): Observable<Object> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'Authorization': this.loggedUser.token_type + ' ' + this.loggedUser.access_token
    });

    return this.http.get(PlusConfig.API.ENDPOINT + `/api/account/sendOTP/${module}/${operation}/${targetMedia}`, { headers: headers });
  }

  public getUserPreviewContacts(tempToken: any): Observable<any> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'Authorization':'Bearer ' + tempToken
    });

    this.spinnerService.show();
    return forkJoin([this.http.get(PlusConfig.API.ENDPOINT + '/api/SigmaApps/CL/Serv/Contacts/E', { headers : headers}),
      this.http.get(PlusConfig.API.ENDPOINT + '/api/SigmaApps/CL/Serv/Contacts/C', { headers : headers})])
      .pipe(
        finalize(() => this.spinnerService.hide())
      );
  }

  public sendOtpCode(otpModel : OtpNotificationModel): Observable<any>{
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'x-api-key': PlusConfig.API.APIKEY
    });

    this.spinnerService.show();
    return this.http.post(PlusConfig.API.ENDPOINT + '/api/account/SendUserOtp', otpModel, { headers : headers})
      .pipe(
        catchError((error) => {
          return this.handleResponseError(error)
        }),
        finalize(() => this.spinnerService.hide())
      );
  }

  public validateOtpCode(otpModel : OtpModel): Observable<any>{
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'x-api-key': PlusConfig.API.APIKEY
    });

    this.spinnerService.show();
    return this.http.post(PlusConfig.API.ENDPOINT + '/api/account/ValidateUserOtp', otpModel, { headers : headers})
      .pipe(
        catchError((error) => {
          return this.handleResponseError(error)
        }),
        finalize(() => this.spinnerService.hide())
      );
  }

  /******************************* CREATE ACCOUNT *******************************/
  public createCustomerAccount(userIdentifier : string, authorizationCode: string, password: string, passwordConfirmation: string): Observable<any> {
    let request: Observable<Object>;

    if (PlusConfig.API.IS_DMZ)
      request = this.buildCreateCustomerAccountDMZRequest(userIdentifier, password, passwordConfirmation, authorizationCode);
    else
      throw new Error("Servicio no disponible para ApiCore");

    this.spinnerService.show();
    return request.pipe(
      catchError((error) => {
        return this.handleResponseError(error)
      }),
      finalize(() => this.spinnerService.hide()));
  }

  private buildCreateCustomerAccountDMZRequest(userIdentifier : string, password: string, passwordConfirmation: string, authorizationCode: string): Observable<Object> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'x-api-key': PlusConfig.API.APIKEY
    });
    let accountSettings = new AccountPasswordModel();
    accountSettings.identifier = userIdentifier;
    accountSettings.newPassword = password;
    accountSettings.confirmPassword = passwordConfirmation;
    accountSettings.authorizationCode = authorizationCode;

    return this.http.post(PlusConfig.API.ENDPOINT + '/api/account/CreateCustomerAccount_new', accountSettings, { headers: headers });
  }

  /******************************* SEND ACCOUNT CREATION LINK *******************************/
  public sendAccountCreationLink(identifier: string, contactVerify: string, targetMedia: TwoFactorAuthentication): Observable<any> {
    let request: Observable<Object>;

    if (PlusConfig.API.IS_DMZ)
      request = this.buildSendAccountCreationLinkDMZRequest(identifier, contactVerify, targetMedia);
    else
      throw new Error("Servicio no disponible para ApiCore");

    this.spinnerService.show();
    return request.pipe(
      catchError((error) => {
        return this.handleResponseError(error)
      }),
      finalize(() => this.spinnerService.hide()));
  }

  private buildSendAccountCreationLinkDMZRequest(identifier: string, contactVerify: string, targetMedia: TwoFactorAuthentication): Observable<Object> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'x-api-key': PlusConfig.API.APIKEY
    });
    let accountSettings = new SendNotificationModel();
    accountSettings.identifier = identifier;
    accountSettings.contactVerify = contactVerify;
    accountSettings.mediaTarget = targetMedia;

    return this.http.post(PlusConfig.API.ENDPOINT + '/api/account/SendAccountCreationLink', accountSettings, { headers: headers });
  }

  /******************************* MISC *******************************/

  private handleResponseError(error) {

    error = error?.error ? error.error : error;
    let errorMsg = (error.ResponseMessages && error.ResponseMessages.length > 0 ? error.ResponseMessages[0].Message : error.description) || error.Message
    let errorCode = (error.ResponseMessages && error.ResponseMessages.length > 0 ? error.ResponseMessages[0].MessageCode : error.code) || error.MessageCode
    return throwError(new PlusException(errorMsg, errorCode, error.Data));

  }

  public isLoggedIn(): boolean {
    return (this.loggedUser != null);
  }

  saveToken(token: TokenResponseModel) {
    this.appStore.dispatch(new SaveLoggedUserAction(token));
    this.setToken(token);
  }

  verifyUrl(authorizationCode: string) {
    let request = this.http.get(`${PlusConfig.API.ENDPOINT}/api/account/CheckAuthLink/${authorizationCode}`, {});

    this.spinnerService.show();
    return request.pipe(
      map(x => x),
      catchError((error) => {
        return this.handleResponseError(error)
      }),
      finalize(() => this.spinnerService.hide()));


  }

  getTermsConditions() {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'x-api-key': PlusConfig.API.APIKEY
    });

    let request = this.http.get(`${PlusConfig.API.ENDPOINT}/api/SigmaApps/CL/Serv/GetTC`, { headers: headers });

    this.spinnerService.show();
    return request.pipe(
      map(x => x),
      catchError((error) => {
        return this.handleResponseError(error)
      }),
      finalize(() => this.spinnerService.hide()));
  }

 public getRecaptcha() {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'x-api-key': PlusConfig.API.APIKEY
    });

    let request = this.http.get(`${PlusConfig.API.ENDPOINT}/api/SigmaApps/CL/Serv/GetRecaptcha`, { headers: headers });
    
    this.spinnerService.show();
    return request.pipe(
      map(x => x),
      catchError((error) => {
        return this.handleResponseError(error)
      }),
      finalize(() => this.spinnerService.hide()));
    }


  public sendGeneratePin(identifier: string): Observable<any> {
    let request: Observable<Object>;

    if (PlusConfig.API.IS_DMZ)
      request = this.sendPin(identifier);
    else
      throw new Error("Servicio no disponible para ApiCore");

    this.spinnerService.show();
    return request.pipe(
      catchError((error) => {
        return this.handleResponseError(error)
      }),
      finalize(() => this.spinnerService.hide()));
  }


  sendPin(userIdentifier: string): Observable<Object> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'x-api-key': PlusConfig.API.APIKEY
    });

    let request = this.http.post(`${PlusConfig.API.ENDPOINT}/api/SigmaApps/CL/Serv/SendPin_new/${userIdentifier}`, null,{ headers: headers });
    return request.pipe(
      map(x => x),
      catchError((error) => {
        return this.handleResponseError(error)
      }),
      finalize(() => this.spinnerService.hide()));
  }

  public sendUnauthenticatedEstCuenta(userIdentifier : string): Observable<Object> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'x-api-key': PlusConfig.API.APIKEY
    });

    let request = this.http.post(`${PlusConfig.API.ENDPOINT}/api/SigmaApps/CL/Serv/AccountStatement/${userIdentifier}`, {}, { headers: headers });
    this.spinnerService.show();
    return request.pipe(
      map(x => x),
      catchError((error) => {
        return this.handleResponseError(error)
      }),
      finalize(() => this.spinnerService.hide()));
  }

   //Seteo de roles en el authService
   private setRequestUserRoles(userCode: string){
    return {
        "Type": 1,
        "UserCode": "SVA",
        "Source": "SVA",
        "Conditions":
        {
          "Data": [{
            "Name": "USER_CODE",
            "Alias": "USER_CODE",
            "Value": userCode,
            "Display": "",
            "Type": 1,
            "FamilyId": ""
          },
          { "Name": "NUM_PATRONO",
          "Alias": "NUM_PATRONO",
          "Value": 0,
          "Display": "",
          "Type": 1,
          "FamilyId": ""
          }
          ]
        },
        "Data": [],
        "Files": [] 
    }
  }


  public requestUserRoles(userCode: string ) {
      this.setUserRoles([]);
      let userRolesRequest = this.setRequestUserRoles(userCode);
      this.httpService.post(HttpService.ENDPOINT + '/api/process/ROLE/SER_GET_USER_ROLES_SVA', userRolesRequest).subscribe({
          next: (response: ApiResponseModel) => {
              let data = response['Data']
              let roleCodes = data[0].ArrayEntities.List.find(item => item.Name === 'ROLE_CODES').Value.split(',');
            this.appStore.dispatch(new SaveUserRolesAction(roleCodes));
            this.setRoles(roleCodes);
            this.userRoles$.next(roleCodes);
          }
        });
  }

  private setRoles(roleCodes: any){
    this.setUserRoles(roleCodes);
    localStorage.setItem('userRoles', roleCodes);
  }

  public getUserOrganizations(userIdentifier: string) {
    this.httpService.post(HttpService.ENDPOINT + '/api/process/USER/SER_GET_USER_ORGANIZATIONS', 
      this.getUserOrganizationsRequest(userIdentifier)).subscribe({
        next: (response: ApiResponseModel) => {
          let organizations : UserOrganizations[] = this.extractOrganizationsFromResponse(response['Data']);
          this.appStore.dispatch(new SaveUserOrganizationsAction(organizations));
          localStorage.setItem('userOrganizations', JSON.stringify(organizations));
          this.userOrganizations = organizations;
        }
      });
  }

  private extractOrganizationsFromResponse(data : any): UserOrganizations[] {
    return data.map(item => {
      const entities = item.ArrayEntities.List;
      let numCliente = 0, nomCompleto = '', roleId = 0, docIdPat = '';

      entities.forEach(entity => {
          if (entity.Name === 'NUM_CLIENTE_PAT') {
              numCliente = parseInt(entity.Value, 10);
          } else if (entity.Name === 'NOM_COMPLETO_PAT') {
              nomCompleto = entity.Value;
          } else if (entity.Name === 'ROLE_ID') {
              roleId = parseInt(entity.Value, 10);
          } else if (entity.Name === 'DOC_ID_PAT') {
              docIdPat = entity.Value;
          }
      });

      return new UserOrganizations(numCliente, nomCompleto, roleId, docIdPat);
   });
  }

  private getUserOrganizationsRequest(userId : string): any {
    return {
        "Type": 1,
        "UserCode": "SVA",
        "Source": "SVA",
        "Conditions":
        {
          "Data": [{
            "Name": "USER_ID",
            "Alias": "USER_ID",
            "Value": userId,
            "Display": "",
            "Type": 1,
            "FamilyId": ""
          },
          ]
        },
        "Data": [],
        "Files": [] 
    }
  }

  refreshUserAfterOrganizationChange(newNumClientePat : number){
    let currToken = this.loggedUser;
    currToken.user.organization = newNumClientePat;
    this.appStore.dispatch(new SaveLoggedUserAction(currToken));
    this.requestUserRoles(currToken.user.user_code);
  }

  public sendTicketInfo(ticketData: any): Observable<any> {
    let headers = new HttpHeaders({
      'x-api-key': PlusConfig.API.APIKEY
    });

    let request = this.http.post(`${PlusConfig.API.ENDPOINT}/api/SigmaApps/CL/Serv/SendTicket`, ticketData, { headers: headers });
  
    this.spinnerService.show();
    return request.pipe(
      map(x => x),
      catchError((error) => {
        return this.handleResponseError(error)
      }),
      finalize(() => this.spinnerService.hide()));
  }

  getToken() { return this.loggedUser }
  getUserRoles(){ return this.userRoles }
  setToken(token: TokenResponseModel): void { this.loggedUser = token }
  setUserRoles(userRoles: string[] ):void {this.userRoles = userRoles}
}
